/**
 * Hardcoded content for the AI transcript review modal flow, transcribed from the
 * Figma spec board "SPEC · AI transcript popup flow — S0 list → S5 confirmation"
 * (2495:3059) and its states S0 2618:3498 / S1 2526:3417 / S2 2526:12196 /
 * S3 2495:3060 / S4 2495:3487 / S5 2499:3123. UI-only — no API. The shapes here
 * are what LiveCallPanel, the embedded sec-card table + sec-editor, and the S5
 * confirmation dialog consume.
 */

/** Subtitle fallback when no patient/appointment is passed in (S0/S1). */
export const PATIENT_FALLBACK = {
  name: "Vineeta Verma",
  phone: "+91 XXXXX X1234",
  pid: "PID 10248",
};

/** Left-rail stat tiles for S2–S4. */
export const STAT_TILES = [
  { id: "review", value: "1/32", label: "In review" },
  { id: "confidence", value: "87%", label: "AI confidence" },
];

/**
 * S2/S3 live transcript chat. Speakers: "care" (CARE MANAGER) / "patient".
 * The reviewing patient turn carries pill-highlighted entities (segments where
 * `entity` is set) plus an "Added to form" footnote.
 */
export const TRANSCRIPT_TURNS = [
  {
    id: "turn-1",
    speaker: "care",
    body: "Hi, can you tell me what's been bothering you most lately?",
  },
  {
    id: "turn-2",
    speaker: "patient",
    reviewing: "REVIEWING · 1 OF 32",
    // Rendered as inline segments; `entity` marks the highlighted spans.
    segments: [
      { text: "I've been having " },
      { text: "back pain", entity: true },
      { text: " for about " },
      { text: "three months", entity: true },
      { text: " now. It's " },
      { text: "mild", entity: true },
      { text: " but happens " },
      { text: "almost daily", entity: true },
    ],
    addedTo: "Chief complaint · LOINC 39111-0",
  },
  {
    id: "turn-3",
    speaker: "care",
    body: "Anything help with the pain? Any medications you're on?",
  },
];

/**
 * Middle-column tab strip. Full 12 v2 sections (matches the spec mock
 * board 2495:3060). The `count` field is unused now — live count comes
 * from CapturedSections at render time (from `capturedItems`).
 */
export const SECTION_TABS = [
  // Patient details is intentionally FIRST — the reviewer's first
  // sanity check on any call is "is this the right patient, and are
  // their basics current?". Wave 1 (2026-09) renders the standalone
  // table + rail edit form only; Wave 2 will bolt on the AI capture +
  // Save & next queue like the other 12 sections.
  { id: "patient-details",   label: "Patient details" },
  { id: "anthropometric",    label: "Anthropometric data" },
  { id: "chief-complaints",  label: "Chief complaints" },
  { id: "diagnosis",         label: "Diagnosis" },
  { id: "medicines",         label: "Medicines" },
  { id: "hospitalisation",   label: "Hospitalisation history" },
  { id: "allergies",         label: "Allergies" },
  { id: "family-history",    label: "Family history" },
  { id: "addictions",        label: "Addictions" },
  { id: "lab-investigations", label: "Lab investigations" },
  { id: "food-preferences",  label: "Food preferences" },
  { id: "daily-schedule",    label: "Daily schedule" },
  { id: "activity",          label: "Physical activity" },
  { id: "drug-schedule",     label: "Drug schedule" },
];

export const ACTIVE_TAB = "patient-details";

/**
 * Chief-complaints table rows for the active tab. `score` drives the VAS band
 * (see ../vasScale); `hasTrend` gates the Analysis "View trend" link — same rule
 * as the standalone Chief complaints section.
 */
export const COMPLAINT_ROWS = [
  {
    id: 1,
    complaint: "Headache",
    since: "29 May 2026",
    description: "Sharp, left-sided; subsides within minutes.",
    score: 8, // Severe (7-9)
    hasTrend: false,
  },
  {
    id: 2,
    complaint: "Low back pain",
    since: "29 May 2026",
    description: "Dull ache in the lower back; worse after sitting for long periods.",
    score: 2, // Mild (1-3)
    hasTrend: false,
  },
  {
    id: 3,
    complaint: "Frequent urination",
    since: "15 Nov 2025",
    description:
      "Occurring multiple times through the day, often noticeable even at night.",
    score: 2, // Mild (1-3)
    hasTrend: true,
  },
  {
    id: 4,
    complaint: "Increased thirst",
    since: "12 Jun 2026",
    description: "Persistent thirst through the day despite adequate water intake.",
    score: 5, // Moderate (4-6)
    hasTrend: true,
  },
  {
    id: 5,
    complaint: "Blurred vision",
    since: "20 May 2026",
    description: "Intermittent blurring, worse in the evenings.",
    score: 3, // Mild (1-3)
    hasTrend: false,
  },
  {
    id: 6,
    complaint: "Tingling in feet",
    since: "02 Apr 2026",
    description: "Pins-and-needles in both feet, more noticeable at night.",
    score: 5, // Moderate (4-6)
    hasTrend: true,
  },
];

/**
 * S3/S4 right editor draft. Reuses the sec-editor archetype: ICD fuzzy search,
 * QuickPick suggestions, VAS status, duration steppers with auto-calc since-when,
 * description.
 */
export const EDITOR_DRAFT = {
  code: "Dx · ICD E11.9",
  query: "Low back pain",
  suggestedFor: "back pain",
  suggestedItems: ["Sciatica", "Lumbar strain", "Herniated disc", "Spinal stenosis"],
  suggestedExtra: 4, // "+4 more"
  vas: "mild", // VAS_BANDS key -> "Mild (1-3)"
  years: 0,
  months: 1,
  days: 12,
  sinceWhen: "08 Jan 2026",
  description:
    "Dull ache in the lower back; worse after sitting for long periods.",
};

/** S4 footer helper line. */
export const FOOTER_TEXT =
  "32 items captured across 6 sections — revisit any tab above, everything stays editable until you save.";

/** S5 confirmation dialog: per-section breakdown summing to 32. */
export const CONFIRM_SUMMARY = {
  total: 32,
  title: "All 32 items added to the patient record",
  breakdown: [
    { label: "Chief complaints", n: 6 },
    { label: "Diagnosis", n: 3 },
    { label: "Medicines", n: 4 },
    { label: "Lab investigations", n: 5 },
    { label: "Allergies", n: 2 },
    { label: "Lifestyle", n: 12 },
  ],
  note: "The popup will close and the record is updated.",
};
